/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import ComponentBase from '../Utility/ComponentBase';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import IDependency from './IDependency';
import StateComponent from './StateComponent';
import { StateID } from './StateID';
import StateManager from './StateManager';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Display class for showing debug information about [[StateManager]]. */
@cc._decorator.ccclass
export default abstract class StateManagerDisplay extends ComponentBase
{
    /** [[StateManager]] to which to display information about. */
    @property(InjectableComponent)
    protected stateManagerRef: InjectableComponent<StateManager> = new InjectableComponent();

    // #region Display Properties

    /** Display for name of node. */
    @property({ type: cc.Label, visible: true })
    private _nodeNameLabel: cc.Label = null;

    /** Display for name of current [[StateComponent]]. */
    @property({ type: cc.Label, visible: true })
    private _currentStateDisplay: cc.Label = null;

    /** Layout for displaying full dependency information. */
    @property({ type: cc.Layout, visible: true })
    private _dependencyLayout: cc.Layout = null;

    private _stateNodePool: cc.NodePool = new cc.NodePool();

    // #endregion

    /** @hidden */
    protected setContexts(): void
    {
        this.stateManagerRef.SetContext(StateManager);
    }

    /** @hidden */
    protected start(): void
    {
        if (!this.stateManagerRef.HasValue())
        {
            CLog.error(`No StateManager configured`, this, LogTypes.Initialization);
            this.enabled = false;
            return;
        }

        this._nodeNameLabel.string = this.node.name;

        let i: number = 10;
        while (i--)
        {
            this._stateNodePool.put(this.getNewDependencyNode()[0]);
        }
    }

    /** @hidden */
    protected update(dt: number): void
    {
        if (!this.stateManagerRef.Value.CurrentState) { return; }

        let currentState: StateComponent = this.stateManagerRef.Value.CurrentState;

        if (this._currentStateDisplay)
        {
            this._currentStateDisplay.string = StateID[currentState.StateID];
        }

        if (this._dependencyLayout)
        {
            this._dependencyLayout.node.children.forEach(child => this._stateNodePool.put(child), this);
            this._dependencyLayout.node.removeAllChildren();

            let dependencies: IDependency[] = currentState.Dependencies.sort((a, b) => {
                let aIsComplete: boolean = a.IsComplete(currentState);
                let bIsComplete: boolean = b.IsComplete(currentState);

                // Sort incomplete first..
                if (!aIsComplete && bIsComplete) { return 1; }
                if (aIsComplete && !bIsComplete) { return -1; }
                return b.NodeName.localeCompare(a.NodeName);
            });

            for (let dependency of dependencies)
            {
                this._dependencyLayout.node.addChild(this.getDependencyNode(dependency,
                    dependency.IsComplete(currentState)));
            }
        }
    }

    private getDependencyNode(dependency: IDependency, isComplete: boolean): cc.Node
    {
        let dependencyNode: cc.Node = this._stateNodePool.get();
        let dependencyLabel: cc.Label;

        if (dependencyNode)
        {
            dependencyLabel = dependencyNode.getComponent(cc.Label);
        }
        else
        {
            let newDependency: [cc.Node, cc.Label] = this.getNewDependencyNode();
            dependencyNode = newDependency[0];
            dependencyLabel = newDependency[1];
        }

        if (isComplete)
        {
            dependencyLabel.string = this.getNodeName(dependency) + " [COMPLETE]";
            dependencyLabel.node.color = cc.Color.GREEN;
        }
        else
        {

            dependencyLabel.string = this.getNodeName(dependency) + " [BLOCKING]";
            dependencyLabel.node.color = cc.Color.RED;
        }

        return dependencyNode;
    }

    private getNewDependencyNode(): [cc.Node, cc.Label]
    {
        const dependencyNode: cc.Node = new cc.Node();
        const newDependencyLabel: cc.Label = dependencyNode.addComponent(cc.Label);

        newDependencyLabel.horizontalAlign = cc.Label.HorizontalAlign.RIGHT;
        newDependencyLabel.fontSize = 18;

        newDependencyLabel.overflow = cc.Label.Overflow.RESIZE_HEIGHT;
        dependencyNode.width = this._dependencyLayout.node.width * .9;

        return [dependencyNode, newDependencyLabel];
    }

    private getNodeName(dependency: IDependency): string
    {
        let nodeName: string = dependency.NodeName;
        const dependencyName: string = dependency['name'];

        const cacheName: string = this._nodeNameCache.get(nodeName + dependencyName);
        if (cacheName) { return cacheName; }

        if (dependencyName)
        {
            const componentName: string = dependencyName.split('<')[1];
            if (componentName
                && componentName.length < 15
                && !componentName.startsWith(nodeName))
            {
                nodeName = dependencyName;
            }
        }

        this._nodeNameCache.set(nodeName + dependencyName, nodeName);
        return nodeName;
    }

    private _nodeNameCache: Map<string, string> = new Map();
}
